import algorithmImage from "../../asset/coding-demo/algorithm.png";
import machineImage from "../../asset/coding-demo/machine.png";
import sqlImage from "../../asset/coding-demo/sql.png";

export const codingData = [
    {
        id: 'algorithm',
        label: 'Data Structure & Algorithm',
        image: algorithmImage,
        content: {
            analysis:`
            <div style="color: #3498db;">
          <p>This question is asking how to determine the player's rank on a leaderboard after each game played.</p>
          <ol>
            <li>Create a unique list of ranks from the ranked scores.</li>
            <li> Initialize a pointer for the ranked list and iterate through the player's scores.</li>
            <li>For each player's score, move the pointer to find the correct rank.</li>
            <li>Store the rank for each player's score.</li>
            <li>Return the list of ranks.</li>
          </ol>
          <p>Finally, it returns the player's ranks after each game.</p>
        </div>`,
            timeComplexity: "O(M+N)",
            spaceComplexity: "O(n)",
            language: "python",
            code: `def climbingLeaderboard(ranked, player):
    # Create a unique list of ranks
    unique_ranks = sorted(set(ranked), reverse=True)
    # Initialize a list to store the ranks of the player
    player_ranks = []
    # Pointer for the unique ranks
    rank_index = len(unique_ranks) - 1
    # Iterate through each score of the player
    for score in player:
        # Move the rank pointer to find the correct rank
        while rank_index >= 0 and score >= unique_ranks[rank_index]:
            rank_index -= 1
        # Append the rank (1-based index)
        player_ranks.append(rank_index + 2)
    return player_ranks`,
            testCases: [
                {
                    input: "[100, 90, 90, 80], [70, 80, 105]",
                    output: "[4, 3, 1]"
                },
                {
                    input: "[100, 90, 90, 80], [70, 80, 90, 95]",
                    output: "[4, 3, 2, 1]"
                },
            ],
        },
    },
    {
        id: 'machine-learning',
        label: 'Machine Learning',
        image: machineImage,
        content: {
            analysis: `
        <div style="color: #3498db;">
          <p>This question is asking for basic statistical measures from a list of integers. We can use simple arithmetic and list operations because they are efficient for this task. Here is a step-by-step analysis:</p>
          <ol>
            <li>Calculate the mean by summing the elements and dividing by the count.</li>
            <li>Determine the median by sorting the list and finding the middle element(s).</li>
            <li>Find the mode by counting occurrences of each element.</li>
            <li>Compute the standard deviation using the formula provided.</li>
            <li>Calculate the confidence interval for the mean.</li>
          </ol>
          <p>Finally, it returns the calculated statistics.</p>
        </div>
       
      `,
            timeComplexity: "O(N log N)",
            spaceComplexity: "O(N)",
            language: "python",
            code: `import numpy as np

class Solution:
    def get_model_prediction(self, X: np.ndarray, weights: np.ndarray) -> np.ndarray:
        # X is an Nx3 NumPy array
        # weights is a 3x1 NumPy array
        # Calculate predictions using matrix multiplication
        predictions = np.matmul(X, weights)
        # Round predictions to 5 decimal places
        return np.round(predictions, 5)

    def get_error(self, model_prediction: np.ndarray, ground_truth: np.ndarray) -> float:
        # Calculate mean squared error
        error = np.mean(np.square(model_prediction - ground_truth))
        # Return rounded error
        return round(error, 5)`,

            testCases: [
                {
                    input: {
                        function: "get_model_prediction",
                        X: [
                            [0.3745401188473625, 0.9507143064099162, 0.7319939418114051]
                        ],
                        weights: [1.0, 2.0, 3.0]
                    },
                    output: [4.47195]
                },
            ],
        },
    },
    {
        id: 'sql',
        label: 'SQL',
        image: sqlImage,
        content: {
             analysis:`
             <div style="color: #3498db;">
  <p>This question is asking the most recent price for each product as of 2019-08-16. Here is a step-by-step analysis:</p>
  <ol>
 <li>Create a CTE that selects all price changes up to the target date.</li>
        <li>Use ROW_NUMBER() to rank changes for each product, with the most recent as rank 1.</li>
<li>Use a subquery to select all unique product IDs from the Products table.</li>   
 <li>LEFT JOIN with LatestPrices to get most recent price change.</li>
         <li>Products without changes will have NULL for the joined price columns.</li>

 <li>Apply COALESCE(lp.new_price, 10) in the SELECT clause.</li>
        <li>Handles both changed and unchanged prices in one operation.</li>   
        <li>Sort the final result set by product_id for consistency.</li>
  </ol>
  <p>This approach efficiently handles products with recent changes, future changes, and no changes.</p>
</div>
`,
            language: "sql",
            code: `WITH LatestPrices AS (
    SELECT 
        product_id,
        new_price,
        ROW_NUMBER() OVER (PARTITION BY product_id ORDER BY change_date DESC) AS rn
    FROM Products
    WHERE change_date <= '2019-08-16'
)
SELECT 
    p.product_id,
    COALESCE(lp.new_price, 10) AS price
FROM 
    (SELECT DISTINCT product_id FROM Products) p
LEFT JOIN LatestPrices lp ON p.product_id = lp.product_id AND lp.rn = 1
ORDER BY p.product_id;`,
            testCases: [
        {
          input: {
            Products: {
              product_id: [1, 1, 1, 1, 2, 2, 3],
              new_price: [20, 30, 35, 50, 50, 65, 20],
              change_date: ["2019-08-14", "2019-08-15", "2019-08-16", "2019-08-14", "2019-08-15", "2019-08-17", "2019-08-18"]
            }
          },
          output: {
            Result: {
              product_id: [1, 2, 3],
              price: [35, 50, 20]
            }
          }
        }
      ],
        },
    }
];
